<template>
  <div id="app" class="bg-bg" :class="appColorTheme">
    <OfflineAlert/>
    <router-view/>
  </div>
</template>

<script>
import Vue from 'vue'
import Fingerprint2 from 'fingerprintjs2'
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";
import OfflineAlert from "@/components/common/OfflineAlert";

export default {
  name: 'App',
  components: {OfflineAlert},
  data() {
    return {
      fpOptions: {
        excludes: {
          adBlock: true,
          screenResolution: false,
          enumerateDevices: true,
        }
      }
    }
  },
  computed: {
    ...mapState('auth', ['user', 'token', 'fp']),
    ...mapState('i18n', ['lang']),
    ...mapGetters('theme', ['appColorTheme']),
  },
  methods: {
    ...mapMutations('auth', ['setFingerPrint', 'setAccessToken', 'logout']),
    ...mapMutations('theme', ['setWindowWidth']),
    ...mapActions('cabinet', ['initCabinet']),
    ...mapActions('i18n', ['changeLang']),
    ...mapActions('auth', ['refresh_tokens']),
    fetchFingerprint() {
      return new Promise((resolve) => {
        setTimeout(() => {
          Fingerprint2.getV18(this.fpOptions, (result, components) => {
            this.setFingerPrint({result, components});
            resolve();
          })
        })
      })
    },
    toastUserHasNewIP(new_ip) {
      this.$toasted.info(`Кажется у вас сменился IP(${new_ip}), проверьте сессии в профиле`,
          {
            duration: 1000 * 15,
            action: {
              text: '×',
              class: 'toasted-action',
              onClick: (e, toastObject) => {
                toastObject.goAway(0);
              }
            }
          });
    },
    registerUnauthorizedInterceptor() {
      Vue.http.interceptors.push((request, next) => {
        next((res) => {
          const refresh_api_url = '/api/auth/refresh';
          const isUnauthorizedStatus = res.status === 401;
          // Unauthorized Access w broken token
          if (isUnauthorizedStatus) {
            if (res.url.includes(refresh_api_url)) {
              this.logout()
            } else {
              this.checkToken();
            }
          }
        });
      });
    },
    async checkToken() {
      let refresh_token = this.$cookies.get('_refresh_token'),
          fingerprint = this.fp.result;
      if (!!refresh_token) {
        try {
          const res = await this.refresh_tokens({refresh_token, fingerprint});
          if (res.body.success) {
            this.setAccessToken(res.body.access_token);
            if (!!res.body.new_ip) this.toastUserHasNewIP(res.body.new_ip)
          }
          return res.body.success;
        } catch (err) {
          console.error(err)
        }
      }
    },
    checkEmailInvite() {
      let {invite} = this.$route.query
      if (invite) {
        localStorage.setItem('email_invite', invite);
        let query = Object.assign({}, this.$route.query);
        delete query.invite;
        this.$router.replace({query});
      }
    },
    accessTokenAutoRefresh(access_exp) {
      if (!!access_exp) {
        let timeout = (access_exp * 1000 - Date.now()) - 1000 * 60;
        setTimeout(() => {
          this.checkToken();
        }, timeout);
      }
    },
    setUserPreferences() {
      if (!!this.$cookies.get('language')) {
        this.changeLang(this.$cookies.get('language'))
      }
      if (!!this.$cookies.get('theme')) {
        this.$store.commit('theme/setColorTheme', this.$cookies.get('theme'))
      }
      if (!!this.$cookies.get('fontTheme')) {
        this.$store.commit('theme/setFontTheme', this.$cookies.get('fontTheme'))
      }
    },
    onResize() {
      this.setWindowWidth(window.innerWidth)
    },
  },
  watch: {
    'token.access_exp': 'accessTokenAutoRefresh',
    lang: {
      immediate: true,
      handler(val) {
        this.$cookies.set('language', val)
      }
    },
  },
  async mounted() {
    this.registerUnauthorizedInterceptor();
    try {
      await this.fetchFingerprint()
      const success = await this.checkToken();
      this.checkEmailInvite();
      // if user is logged in then init cabinet
      if (success) {
        await this.initCabinet();
      } else {
        this.setUserPreferences();
      }
    } catch (err) {
      console.error(err);
    }
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize);
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  }
}
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css?family=Roboto:400,500,700,900&display=swap');

html {
  scroll-behavior: smooth;
  font-size: 10px;
}

body {
  margin: 0;
  font-family: 'Roboto', sans-serif;
  background: var(--bg-background-primary);
}

a:focus, button:focus {
  outline: none
}

.grecaptcha-badge {
  visibility: hidden;
}

#app {
  min-height: 100vh;
  line-height: 1.2;
}

:root {
  --bg-background-primary: #EDF1F5;
  --bg-background-secondary: #E0E9ED;
  --bg-primary-block: #ffffff;
  --bg-secondary-block: #F8FAFC;
  --bg-contractor: #2D4C5E;

  --shadow-primary-block: 0px 4px 8px rgba(86, 114, 155, 0.1);
  --shadow-popup: 0px 8px 16px rgba(86, 114, 155, 0.15);
  --shadow-input: inset 2px 2px 6px rgba(0, 0, 0, 0.05);;

  --border-primary: #DFE9ED;
  --border-hover: #C7D6DC;

  --line-primary: #F0F4F7;

  --highlight-primary: #F6F8FA;

  --text-text-default: #2D4C5E;
  --text-text-secondary: #444444;
  --text-text-ternary: #8A9BA5;
  --text-text-label: #2E607C;
  --text-text-muted: #8B9AA9;
}

.theme-dark {
  --bg-background-primary: #1F313F;
  --bg-background-secondary: #E0E9ED;
  --bg-primary-block: #263744;
  --bg-secondary-block: #2c3b4a;

  --shadow-primary-block: 0px 4px 8px rgba(104, 117, 134, 0.1);
  --shadow-popup: 0px 8px 16px rgba(104, 117, 134, 0.15);
  --shadow-input: inset 2px 2px 6px rgba(0, 0, 0, 0.05);;

  --border-primary: #2c4756;
  --border-hover: #C7D6DC;

  --line-primary: #2c4150;

  --highlight-primary: #405061;

  --text-text-default: white;
  --text-text-secondary: #dedede;
  --text-text-ternary: #dedede;
  --text-text-label: #b4cad7;
  --text-text-muted: #8B9AA9;
}

.tooltip {
  display: block !important;
  z-index: 10000;
  font-size: 11px;

  .tooltip-inner {
    background: black;
    color: white;
    border-radius: 4px;
    padding: 4px 16px;
    text-align: center;
  }

  .tooltip-arrow {
    width: 0;
    height: 0;
    border-style: solid;
    position: absolute;
    margin: 5px;
    border-color: black;
    z-index: 1;
  }

  &[x-placement^="top"] {
    margin-bottom: 5px;

    .tooltip-arrow {
      border-width: 5px 5px 0 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      bottom: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[x-placement^="bottom"] {
    margin-top: 5px;

    .tooltip-arrow {
      border-width: 0 5px 5px 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-top-color: transparent !important;
      top: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[x-placement^="right"] {
    margin-left: 5px;

    .tooltip-arrow {
      border-width: 5px 5px 5px 0;
      border-left-color: transparent !important;
      border-top-color: transparent !important;
      border-bottom-color: transparent !important;
      left: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }

  &[x-placement^="left"] {
    margin-right: 5px;

    .tooltip-arrow {
      border-width: 5px 0 5px 5px;
      border-top-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      right: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }

  &.popover {

    .popover-inner {
      @apply bg-block text-text py-4 px-6 rounded shadow-primaryBlock;
    }

    .popover-arrow {
      @apply border-block;
    }
  }

  &[aria-hidden='true'] {
    visibility: hidden;
    opacity: 0;
    transition: opacity .15s, visibility .15s;
  }

  &[aria-hidden='false'] {
    visibility: visible;
    opacity: 1;
    transition: opacity .15s;
  }

  &.--white {
    &[x-placement^="top"] {
      margin-bottom: 8px;
    }

    .tooltip-inner {
      @apply bg-block text-text;
      border-radius: 2px;
      box-shadow: 0px 2px 8px rgba(82, 89, 112, 0.17);
      padding: 7px 10px;
    }

    .tooltip-arrow {
      @apply border-block;
      border-width: 8px 8px 0 8px;
      bottom: -8px;
    }
  }
}

.toasted-container .toasted .action {
  font-size: 2.3rem !important;

  &:hover {
    color: white !important;
    text-decoration: none !important;
  }
}

.router-anim-enter-active {
  animation: routerInUp .4s ease-in;
  animation-delay: .2s;
  opacity: 0;
}

.router-anim-leave-active {
  animation: routerOutDown .3s ease-out;
}

#silentbox-gallery {
  &.--mk-theme {
    #silentbox-overlay__background {
      background: rgba(45, 76, 94, 0.1);
      backdrop-filter: blur(10px);
    }

    #silentbox-overlay__embed img, #silentbox-overlay__embed iframe {
      box-shadow: none;
    }

    #silentbox-overlay__close-button:hover,
    #silentbox-overlay__close-button:focus {
      background: transparent;
    }

    #silentbox-overlay__close-button .icon {
      @apply h-8 w-8;
      left: -0.6rem;
    }

    #silentbox-overlay__close-button .icon:before, #silentbox-overlay__close-button .icon:after {
      @apply bg-red rounded;
      height: 3px;
    }

    #silentbox-overlay__close-button .icon:hover:before,
    #silentbox-overlay__close-button .icon:hover:after,
    #silentbox-overlay__close-button .icon:focus:before,
    #silentbox-overlay__close-button .icon:focus:after {
      @apply bg-red-hover;
      width: 100%;
      left: 0.1rem;
    }
  }
}

@keyframes routerInUp {
  from {
    opacity: 0;
    transform: translateY(50px)
  }
  to {
    opacity: 1;
    transform: translateY(0)
  }
}

@keyframes fadeOutLeft {
  0% {
    opacity: 1;
    transform: translateX(0)
  }
  to {
    opacity: 0;
    transform: translateX(-50px)
  }
}

@keyframes routerOutDown {
  0% {
    opacity: 1;
    transform: translateY(0)
  }
  to {
    opacity: 0;
    transform: translateY(50px)
  }
}
</style>
